/* eslint-disable multiline-ternary */
/* eslint-disable @typescript-eslint/restrict-template-expressions */
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { AmplitudeService } from '@inyova/inyova-shared';
import { Customer, CustomerAccount, CustomerAccountItem, FeatureFlags, PortfolioAllocationV2 } from '@inyova/models';
import { isCustomer, isInvestAccount } from '@inyova/utils';
import { IonRouterOutlet, ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';

import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as AccountActions from '@account/account.actions';
import * as RootActions from '@app/app.actions';
import * as HomeActions from '@home/home.actions';
import * as StrategyActions from '@strategy/strategy.actions';
import * as fromAccount from '@account/account.reducers';
import * as fromRoot from '@app/app.reducers';
import * as fromStrategy from '@strategy/strategy.reducers';
import * as fromHome from '@home/reducers';

import { formatDate } from '@app/home/home.utils';
import { HomeBalanceSheetModalComponent, HomePerfChartSettingsModalComponent } from '@app/home/modals';
import { PortfolioAllocation } from '@app/shared/models/Strategy';
import { StatusBarStylingService } from '@app/shared/services/status-bar-styling.service';
import {
  getModalPerformanceFilters,
  isAnnualPerformanceRange,
  performanceEventsMap
} from '@home/components/home-performance-chart/home-performance-chart.util';
import { CompanyDetailComponent } from '@shared/modals';
import { StockCompanyDetailComponent } from '@shared/modals/company-detail/stock-company-detail.component';
import {
  CompaniesStockPerformance,
  CrowdInvestingValues,
  PerformanceChart,
  PerformanceFilterRangeValue,
  PerformanceGraphItem,
  PerformanceMarketUpdates,
  PerformanceSummary,
  PerformanceType,
  ReportEndValues,
  Stock,
  TechnicalIssuesReporting
} from '@shared/models/Home';
import { State } from '@shared/models/State';
import { TrackingService } from '@shared/services/tracking.service';

enum TabList {
  performance,
  strategy,
  activity,
  risk
}

@Component({
  selector: 'app-home-performance',
  styleUrls: ['home-performance.component.scss'],
  templateUrl: './home-performance.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class HomePerformanceComponent implements OnDestroy {
  protected readonly onDestroy$ = new Subject<void>();
  readonly performanceSummary$: Observable<PerformanceSummary> = this.store.select(fromHome.selectPerformanceSummary);
  readonly technicalIssues$: Observable<TechnicalIssuesReporting> = this.store.select(fromHome.selectTechnicalIssuesReporting);
  readonly companiesStockPerformance$: Observable<CompaniesStockPerformance[]> = this.store.select(fromHome.selectCompaniesStockPerformance);

  readonly accountStocks$: Observable<Stock[]> = this.store.select(fromHome.selectAccountStocks);
  readonly availableAnnualFilters$: Observable<number[]> = this.store.select(fromHome.selectAvailableAnnualFilters);
  readonly crowdInvestingValues$: Observable<CrowdInvestingValues> = this.store.select(fromHome.selectCrowdInvestorData);
  readonly performance$: Observable<PerformanceChart> = this.store.select(fromHome.selectPerformance);
  readonly performanceData$: Observable<PerformanceGraphItem[]> = this.store.select(fromHome.selectPerformanceData);
  readonly accountsMaxGraphDataLoaded$: Observable<boolean> = this.store.select(fromHome.selectAccountsMaxGraphDataLoaded);
  readonly isCrowdInvestorAccountActive$: Observable<boolean> = this.store.select(fromRoot.selectCrowdInvestorAccountActive);
  readonly endValues$: Observable<ReportEndValues> = this.store.select(fromHome.selectEndValuesData);
  readonly performanceAllValue$: Observable<number> = this.store.select(fromHome.selectPerformanceAll);
  readonly isAccountLoading$: Observable<{ show: boolean; message: string }> = this.store.select(fromAccount.selectIsAccountLoading);
  readonly isEve2$: Observable<boolean> = this.store.select(fromAccount.selectAccountFeatureFlag(FeatureFlags.EVE2));
  readonly marketUpdates$: Observable<PerformanceMarketUpdates> = this.store.select(fromHome.selectPerformanceMarketUpdates);
  readonly showEve2Callout$: Observable<boolean> = this.store.select(
    fromAccount.selectAccountFeatureFlag(FeatureFlags.STRATEGY_HIDDEN_EVE2_MIGRATION)
  );

  readonly hasPortfolioUpdatesFlag$: Observable<boolean> = this.store.select(fromAccount.selectAccountFeatureFlag(FeatureFlags.PORTFOLIO_UPDATES));

  currentAccount!: CustomerAccount;
  selectedAccount: CustomerAccountItem;
  customer!: Customer;
  minimumInvestment = 0;

  eventsMap = performanceEventsMap;

  contentTabList = TabList;
  switcherTabs = [
    { id: this.contentTabList.performance, value: 'PERFORMANCE.tabName' },
    { id: this.contentTabList.strategy, value: 'STRATEGY.tabName' }
    // TODO: Active them when we have content
    // { id: this.contentTabList.activity, value: 'translation.key' },
    // { id: this.contentTabList.risk, value: 'translation.key' }
  ];

  activeTab: number | string = this.switcherTabs[0].id;
  showStockPerfInfo: boolean;
  hasIssue: boolean;
  companiesStockPerformanceInitialised = false;

  homeStrategyDetails$: Observable<{ riskLevel: string; nextStrategyChangeAt: string }>;
  portfolioAllocation$: Observable<PortfolioAllocation>;
  portfolioAllocationV2$: Observable<PortfolioAllocationV2>;

  infoList = {
    updateDate: false
  };

  constructor(
    private amplitudeService: AmplitudeService,
    private modalController: ModalController,
    private translateService: TranslateService,
    private routerOutlet: IonRouterOutlet,
    private store: Store<State>,
    private trackingService: TrackingService,
    private router: Router,
    private statusBarStyling: StatusBarStylingService,
    private cd: ChangeDetectorRef
  ) {
    this.store.pipe(select(fromRoot.selectSelectedAccount), takeUntil(this.onDestroy$)).subscribe((account) => {
      this.selectedAccount = account;
      if (account?.id?.length > 0 && account?.kind !== 'grow') {
        this.store.dispatch(HomeActions.ensurePerformanceAccountData());
        this.store.dispatch(HomeActions.loadPerformanceFilters());
        this.fetchData();
      }
    });

    this.store.pipe(select(fromAccount.selectCustomerAndCurrentAccount), takeUntil(this.onDestroy$)).subscribe((value) => {
      this.currentAccount = value.currentAccount as unknown as CustomerAccount;
      this.customer = value.customer;

      if (!isInvestAccount(value.currentAccount)) {
        return;
      }

      this.minimumInvestment =
        this.customer.app_location === 'de' && this.currentAccount.has_low_investment_balance ? 2000 : this.currentAccount.minimum_investment;

      this.store.dispatch(RootActions.getFundBreakdowns());

      // call only when we know customers
      if (!this.companiesStockPerformanceInitialised && this.customer.accounts?.length) {
        this.customer.accounts.forEach((account) => {
          if (isCustomer(account)) {
            if (!account.enabled_features.includes(FeatureFlags.EVE2)) {
              this.store.dispatch(HomeActions.getCompaniesStockPerformance({ account }));
            }
          }
        });
        this.companiesStockPerformanceInitialised = true;
      }
    });

    combineLatest([this.technicalIssues$, this.store.select(fromAccount.selectCustomerAndCurrentAccount)])
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(([issues, { customer, currentAccount }]) => {
        if (!customer?.app_location || !currentAccount?.id) {
          return;
        }

        this.hasIssue = issues[`${customer.app_location}${currentAccount.kind}`];
        this.cd.markForCheck();
      });

    // fetch only init
    this.store.dispatch(HomeActions.getAllReportingsStatus());

    this.homeStrategyDetails$ = this.store.select(fromStrategy.selectHomeStrategyDetails);
    this.portfolioAllocation$ = this.store.select(fromStrategy.selectPortfolioAllocation);
    this.portfolioAllocationV2$ = this.store.select(fromStrategy.selectPortfolioAllocationV2);
  }

  ionViewDidEnter() {
    if (!!this.selectedAccount && this.currentAccount.id !== this.selectedAccount.id) {
      this.store.dispatch(
        AccountActions.setSwitchAccountLoading({
          show: true,
          message: this.translateService.instant(`SHARED.accountSwitchSpinner.${this.selectedAccount.kind}`, {
            kidsName: this.selectedAccount.owner_name
          })
        })
      );
      this.store.dispatch(AccountActions.getCurrentAccount({ id: this.selectedAccount.id }));
    }

    this.store.dispatch(RootActions.updateFundBreakdownsOnLanguageChange());
    this.store.dispatch(HomeActions.getPerformanceMarketUpdate());
  }

  formatDate(date: string, seperator: '.' | '-' = '.') {
    return formatDate(date, seperator, this.translateService.currentLang);
  }

  showInfo(item: string): void {
    this.infoList[item] = !this.infoList[item];
  }

  fetchData(): void {
    this.store.dispatch(StrategyActions.getInvestmentData());
    this.store.dispatch(StrategyActions.getInvestmentDetails());
    this.store.dispatch(StrategyActions.getPortfolioAllocation());
    this.store.dispatch(HomeActions.getPerformanceSummary());
    this.store.dispatch(HomeActions.getBalanceSheet());
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  onOpenLink(event: MouseEvent, id: number) {
    event.preventDefault();
    this.amplitudeService.trackEvent('Performance Opened', { market_update_interaction: 'opened' });
    this.router.navigate(['tabs', 'performance', 'market-updates', id]);
  }

  onDismiss(event: MouseEvent, id: number) {
    event.preventDefault();
    this.amplitudeService.trackEvent('Performance Opened', { market_update_interaction: 'dismissed' });
    this.store.dispatch(HomeActions.dismissMarketUpdate({ id }));
  }

  calculateAmount(minInvestment: number, depositsValue: number, withdrawalAll: number): number {
    return minInvestment - (depositsValue - withdrawalAll);
  }

  track(event: string) {
    this.trackingService.trackActivity(event);
  }

  onFilterChange(range: PerformanceFilterRangeValue, currentRange: PerformanceFilterRangeValue, chartType: PerformanceType) {
    this.track(`[Performance chart] Change filter: ${range || 'ALL'}`);
    this.amplitudeService.trackEvent(`Performance Graph Engaged`, {
      performance_chart_time_horizon: this.eventsMap[range] ?? 'calendar_year',
      kind: this.currentAccount.kind
    });

    this.amplitudeService.trackEvent(`Time Frame Selected`, {
      performance_chart_time_horizon: this.eventsMap[range] ?? 'calendar_year',
      previous_time_horizon: this.eventsMap[currentRange] ?? 'calendar_year',
      ...(isAnnualPerformanceRange(range) && { selected_year: Number(range) }),
      active_value_view: this.eventsMap[chartType],
      kind: this.currentAccount.kind
    });
    this.store.dispatch(HomeActions.getPerformanceGraph(range));
  }

  onTypeChange(chartType: PerformanceType) {
    this.track(`[Performance chart] Selected chart: ${chartType}`);
    this.amplitudeService.trackEvent(`Performance Graph Engaged`, {
      performance_chart_ctas: chartType === PerformanceType.PERFORMANCE ? 'percentage_selected' : 'chf_selected',
      kind: this.currentAccount.kind
    });
    this.store.dispatch(HomeActions.togglePerformanceGraphType({ chartType }));
  }

  async onOpenPerfSettings(
    { showBenchmark }: { showBenchmark: boolean },
    availableAnnualFilters: number[],
    currentRange: PerformanceFilterRangeValue,
    chartType: PerformanceType,
    showPortfolioChanges: boolean,
    hasPortfolioUpdatesFlag: boolean
  ) {
    this.amplitudeService.trackEvent(`Portfolio Chart Settings Opened`, {
      current_benchmark: showBenchmark ? 'inyova_all_stocks_index' : 'none',
      current_time_horizon: this.eventsMap[currentRange] ?? 'calendar_year',
      current_activity_marks_shown: showPortfolioChanges,
      kind: this.currentAccount.kind
    });

    const modalPerformanceFilters = getModalPerformanceFilters(availableAnnualFilters);

    const modal = await this.modalController.create({
      component: HomePerfChartSettingsModalComponent,
      showBackdrop: true,
      canDismiss: async (data?: undefined, role?: string) => role !== 'gesture',
      backdropDismiss: true,
      componentProps: {
        showBenchmark,
        showPortfolioChanges,
        modalPerformanceFilters,
        currentRange,
        kind: this.currentAccount.kind,
        hasPortfolioUpdatesFlag
      },
      cssClass: 'auto-height'
    });

    modal.onDidDismiss().then(({ data }) => {
      if (typeof data !== 'undefined') {
        if (typeof data.selectedRange !== 'undefined') {
          this.onFilterChange(data.selectedRange, currentRange, chartType);
        }

        if (typeof data.showPortfolioChanges !== 'undefined') {
          this.amplitudeService.trackEvent(`Portfolio Chart Activity Marks Toggled`, {
            is_shown: data.showPortfolioChanges,
            kind: this.currentAccount.kind
          });
          this.store.dispatch(HomeActions.togglePerformanceEvents(data.showPortfolioChanges));
        }

        if (typeof data.showBenchmark !== 'undefined') {
          this.amplitudeService.trackEvent(`Benchmark Selected`, {
            current_benchmark: data.showBenchmark ? 'inyova_all_stocks_index' : 'none',
            previous_benchmark: showBenchmark ? 'inyova_all_stocks_index' : 'none',
            kind: this.currentAccount.kind
          });
          this.store.dispatch(HomeActions.togglePerformanceBenchmarkType(data.showBenchmark));
        }
      }
    });
    return modal.present();
  }

  onGraphInteraction({ cta, range }: { cta: 'date_scrubbed' | 'date_tapped' | 'portfolio_change_card_opened'; range: PerformanceFilterRangeValue }) {
    this.amplitudeService.trackEvent(`Performance Graph Engaged`, {
      performance_chart_ctas: cta,
      performance_chart_time_horizon: this.eventsMap[range] ?? 'calendar_year',
      ...(isAnnualPerformanceRange(range) && { selected_year: Number(range) }),
      kind: this.currentAccount.kind
    });
  }

  onInvestDetails() {
    void this.router.navigate(['/tabs/account/invest-details']);
  }

  switchTabs(id: number | string) {
    this.track(`[Performance] Tab Clicked: ${TabList[id]}`);

    let tab = '';
    if (TabList[id] === 'performance') {
      tab = 'performance_tab_opened';
    } else if (TabList[id] === 'strategy') {
      tab = 'strategy_tab_opened';
    }

    this.amplitudeService.trackEvent(`Performance Graph Engaged`, { performance_chart_ctas: tab, kind: this.currentAccount.kind });
    this.activeTab = id;
  }

  switchStockPerfInfo(): void {
    this.showStockPerfInfo = !this.showStockPerfInfo;
  }

  async showCompanyDetail($event: { companyID: number; title: string }) {
    this.amplitudeService.trackEvent('Company Profile Opened', { kind: this.currentAccount.kind });
    this.trackingService.trackActivity(`[List] Top 3 Companies: ${$event.title}`);
    this.statusBarStyling.setBackgroundColor('dialog');
    this.store.dispatch(StrategyActions.getCompanyDetail({ companyId: $event.companyID }));
    const modal = await this.modalController.create({
      component: CompanyDetailComponent,
      cssClass: 'app-fullscreen',
      presentingElement: this.routerOutlet.nativeEl
    });
    void modal.onDidDismiss().then(() => {
      this.statusBarStyling.setBackgroundColor('base');
    });
    return modal.present();
  }

  async onViewCompanyDetail(stock: Stock) {
    this.amplitudeService.trackEvent('Company Profile Opened', { kind: this.currentAccount.kind });
    this.trackingService.trackActivity(`[List] Top 3 Companies: ${stock.company.name}`);
    this.statusBarStyling.setBackgroundColor('dialog');

    const modal = await this.modalController.create({
      component: StockCompanyDetailComponent,
      cssClass: 'app-fullscreen',
      presentingElement: this.routerOutlet.nativeEl,
      componentProps: {
        companyDetail: stock.company
      }
    });
    void modal.onDidDismiss().then(() => {
      this.statusBarStyling.setBackgroundColor('base');
    });
    return modal.present();
  }

  async openBalanceSheet(): Promise<void> {
    this.track(`[Button]: Opened Balance Sheet Modal`);
    this.amplitudeService.trackEvent('Portfolio Report Opened', { kind: this.currentAccount.kind });

    const modal = await this.modalController.create({
      component: HomeBalanceSheetModalComponent,
      componentProps: {
        currency: this.currentAccount.currency,
        language: this.translateService.currentLang,
        appLocation: this.customer.app_location
      },
      presentingElement: this.routerOutlet.nativeEl
    });
    return modal.present();
  }
}
